import { useCallback } from 'react'
import { WorkspaceIcon } from '@/components/layout/left-sidebar.components'
import {
  useWorkspaceRowContextMenu,
  WorkspaceRowContextMenuPortal,
} from '@/components/layout/left-sidebar.workspace-row-menu'
import { useWorkspace } from '@/providers/workspace-provider'
import { testIds } from '@/lib/test-ids'
import { WS_ICON_ON_ACCENT, workspaceAccentFill } from '@/lib/workspace-color'

export interface WorkspaceChipRowProps {
  /** Omit this workspace from the row (e.g. the workspace already shown in its own dashboard header). */
  excludeWorkspaceId?: string
  className?: string
}

/**
 * "Active workspaces" chip row - shared by the global Home page and the
 * per-workspace dashboard's quick-switcher. Always reflects the live
 * `activeWorkspaceIds` scope from `useWorkspace()`, so hiding/showing a
 * workspace here stays in sync with the Browse sidebar.
 *
 * Right-click reuses the exact same generic workspace context menu as the
 * Browse sidebar's workspace rows (`left-sidebar.workspace-row-menu.tsx`).
 */
export function WorkspaceChipRow({ excludeWorkspaceId, className }: WorkspaceChipRowProps) {
  const { activeWorkspaces, activeWorkspaceIds, toggleWorkspace, hideOtherWorkspaces } = useWorkspace()
  const { menu, menuRef, openMenu, closeMenu } = useWorkspaceRowContextMenu()

  const chips = activeWorkspaces.filter((ws) => ws.id !== excludeWorkspaceId)

  const handleActivate = useCallback((workspaceId: string) => {
    window.dispatchEvent(new CustomEvent('open-workspace-home', { detail: { workspaceId } }))
  }, [])

  if (chips.length === 0) return null

  return (
    <>
      <div className={className ?? 'flex flex-wrap gap-3'} data-testid={testIds.homeWorkspaceChips.row}>
        {chips.map((ws) => {
          const wsColor = ws.color ?? 'hsl(var(--primary))'
          return (
            <div
              key={ws.id}
              role="button"
              tabIndex={0}
              data-testid={testIds.homeWorkspaceChips.chip(ws.id)}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-border bg-background hover:bg-accent/40 transition-colors cursor-pointer"
              style={{ borderLeftWidth: 3, borderLeftColor: wsColor }}
              onClick={() => handleActivate(ws.id)}
              onContextMenu={(e) => openMenu(ws.id, e)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleActivate(ws.id)
                }
              }}
            >
              <span
                className="w-6 h-6 rounded flex items-center justify-center flex-none"
                style={{ backgroundColor: workspaceAccentFill(wsColor) }}
              >
                <WorkspaceIcon icon={ws.icon} color={WS_ICON_ON_ACCENT} name={ws.name} size={14} />
              </span>
              <p className="text-sm font-medium leading-none truncate max-w-[10rem]">{ws.name}</p>
            </div>
          )
        })}
      </div>
      <WorkspaceRowContextMenuPortal
        menu={menu}
        menuRef={menuRef}
        onClose={closeMenu}
        onToggleWorkspace={toggleWorkspace}
        onHideOtherWorkspaces={hideOtherWorkspaces}
        activeWorkspaceIds={activeWorkspaceIds}
      />
    </>
  )
}
